import { NotificationsActiveOutlined } from '@mui/icons-material';
import { CircularProgress, List, Stack, styled } from '@mui/material';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import React from 'react';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useNotificationSettings } from 'features/settings/hooks/useNotificationSettings';
import { useSettingsModal } from 'contexts/SettingsModalContext/useSettingsModal';
import { SettingsListSubheader } from 'components/Settings/SettingsListSubheader';
import { LinkItem } from 'components/LinkItem/LinkItem';
import { SETTINGS_SECTION_IDS } from 'features/settings/types/types';
import { ErrorState } from 'components/ErrorState/ErrorState';
import UnExpectedErrorTwo from 'assets/images/ErrorState/UnexpectedErrorTwo.svg';
import { Alert } from 'components/Alert/Alert';
import ErrorIcon from '@mui/icons-material/Error';
import { NotificationPreferencesContent } from './NotificationPreferencesContent';

const CenterContentContainer = styled(Stack)(({ theme }) => ({
  justifyContent: 'center',
  alignItems: 'center',
  flexGrow: 1,
  height: '100%',
  paddingX: theme.spacing(2),
}));

export const NotificationSettings = () => {
  const { openModal } = useSettingsModal();
  const { isDesktop } = useDeviceType();
  const { translation } = useTranslationContext();
  const {
    enabledInExternalSystem,
    enabled,
    chatNotificationEnabled,
    memberNotificationEnabled,
    memberSmsEnabled,
    memberLoaded,
    onChangeNotificationPreferences,
    userPhoneNumber,
    hospitalTwilioSmsNumber,
    onRetryAction,
    serverErrorHeading,
    serverErrorBody,
    dismissAlert,
    isNotificationSettingsLoading,
    isNotificationSettingsError,
    alertMessage,
  } = useNotificationSettings({ enableAutoCreate: true });
  const handleModalOpen = () => {
    openModal(SETTINGS_SECTION_IDS.NOTIFICATIONS, translation('notification_preferences'));
  };
  return (
    <List>
      <SettingsListSubheader
        aria-label={translation('notification_preferences')}
        isDesktop={isDesktop}
        id="notification__settings-header"
      >
        {translation('notification_preferences')}
      </SettingsListSubheader>
      {isNotificationSettingsLoading && (
        <CenterContentContainer>
          <CircularProgress
            aria-label="Loading"
            role="img"
            className="notification-settings-loading-spinner"
          />
        </CenterContentContainer>
      )}
      {isNotificationSettingsError && !isNotificationSettingsLoading && (
        <CenterContentContainer>
          <ErrorState
            imageArray={[UnExpectedErrorTwo]}
            onRetryClick={onRetryAction}
            title={serverErrorHeading}
            subtitle={serverErrorBody}
          />
        </CenterContentContainer>
      )}
      {!isNotificationSettingsError && isDesktop && !isNotificationSettingsLoading && (
        <NotificationPreferencesContent
          enabledInExternalSystem={enabledInExternalSystem}
          userPhoneNumber={userPhoneNumber}
          enabled={enabled}
          chatNotificationsEnabled={chatNotificationEnabled}
          memberNotificationEnabled={memberNotificationEnabled}
          memberSmsEnabled={memberSmsEnabled}
          memberLoaded={memberLoaded}
          hospitalTwilioSmsNumber={hospitalTwilioSmsNumber}
          onChangeNotificationPreferences={onChangeNotificationPreferences}
        />
      )}
      {!isNotificationSettingsError && !isDesktop && !isNotificationSettingsLoading && (
        <LinkItem
          showLinkIcon={false}
          isButton
          onClick={handleModalOpen}
          leftIcon={<NotificationsActiveOutlined color="secondary" />}
        >
          {translation('notification_preferences')}
        </LinkItem>
      )}
      <Alert
        isPresented={!!alertMessage}
        variant="filled"
        severity="error"
        message={alertMessage}
        icon={<ErrorIcon />}
        handleDismiss={dismissAlert}
      />
    </List>
  );
};
